import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import {
  Autocomplete,
  Box,
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Stack,
  TextField,
  FormControl,
  FormControlLabel,
  Select,
  Typography,
  CircularProgress,
  Backdrop,
  Switch,
  useTheme,
  FormHelperText,
  Chip,
} from "@mui/material";
import { DatePicker } from "antd";
import ImageUploader from "../../../../../components/ImageUploader";

import * as Yup from "yup";
import { Formik, Field } from "formik";
import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";

import { openSnackbar } from "../../../../../api/snackbar";
import MainCard from "../../../../../components/MainCard";

import { SnackbarProps } from "../../../../../types/snackbar";

import { USERAXIOS } from "../../../../../api/userAxios";
import { City, Country } from "../../../../../types/site-params/Country";

import { useDebounce } from "../../../../../hooks";
import { useUsersForAutocomplete } from "../../../../../hooks/query-hooks/useUsers";
import { useCountriesForAutocomplete } from "../../../../../hooks/query-hooks/useCountries";
import { useCitiesForAutocomplete } from "../../../../../hooks/query-hooks/useCities";

import { useJobTypesForAutocomplete } from "../../../../../hooks/query-hooks/useJobTypes";
import { useDegreeTypesForAutocomplete } from "../../../../../hooks/query-hooks/useDegreeTypes";
import { useJobActivitiesForAutocomplete } from "../../../../../hooks/query-hooks/useJobActivities";
import PdfUploader from "../../../../../components/PdfUploader";
import {
  genderOptions,
  militaryServiceOptions,
  mockVacancyProfile,
  salaryTypeOptions,
  statusOptions,
  vacancyLangOptions,
  vacancyResumeLangOptions,
} from "./mockData";

const AddEditVacancy = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { vacancyId } = useParams();

  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [selectedCity, setSelectedCity] = useState<number | null>(null);
  const [profileData, setProfileData] = useState(mockVacancyProfile);
  const [isLoading, setIsLoading] = useState(false);
  const [isMilitaryServiceDisabled, setIsMilitaryServiceDisabled] =
    useState(false);

  const [serverErrors, setServerErrors] = useState<Record<string, string[]>>(
    {}
  );

  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [jobTypeSearchTerm, setJobTypeSearchTerm] = useState("");
  const [degreeTypeSearchTerm, setDegreeTypeSearchTerm] = useState("");
  const [jobActivitySearchTerm, setJobActivitySearchTerm] = useState("");

  const debouncedCountrySearchTerm = useDebounce(countrySearchTerm, 500);
  const debouncedCitySearchTerm = useDebounce(citySearchTerm, 500);
  const debouncedUserSearchTerm = useDebounce(userSearchTerm, 500);
  const debouncedJobTypeSearchTerm = useDebounce(jobTypeSearchTerm, 500);
  const debouncedDegreeTypeSearchTerm = useDebounce(degreeTypeSearchTerm, 500);
  const debouncedJobActivitySearchTerm = useDebounce(
    jobActivitySearchTerm,
    500
  );

  const {
    items: countries,
    loading: loadingCountries,
    hasMore: hasMoreCountries,
    loadMore: loadMoreCountries,
    error: countriesError,
  } = useCountriesForAutocomplete(20, debouncedCountrySearchTerm);

  const {
    items: cities,
    loading: loadingCities,
    hasMore: hasMoreCities,
    loadMore: loadMoreCities,
    error: citiesError,
  } = useCitiesForAutocomplete(selectedCountry, 20, debouncedCitySearchTerm);

  const { items: users = [] } = useUsersForAutocomplete(
    "COMPANY",
    20,
    debouncedUserSearchTerm
  );

  const { items: jobTypes = [] } = useJobTypesForAutocomplete(
    20,
    debouncedJobTypeSearchTerm
  );

  const { items: degreeTypes = [] } = useDegreeTypesForAutocomplete(
    20,
    debouncedDegreeTypeSearchTerm
  );

  const { items: jobActivities = [] } = useJobActivitiesForAutocomplete(
    20,
    debouncedJobActivitySearchTerm
  );

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    company_description: Yup.string(),
    email: Yup.string().email("Invalid email format"),
    employees_total_numbers: Yup.number().min(1, "Must be at least 1"),
    duration: Yup.string().required("Duration is required"),
    responsibilities: Yup.string().required("Responsibilities are required"),
    gender: Yup.string().required("Gender preference is required"),
    vacancy_lang: Yup.string().required("Vacancy language is required"),
    status: Yup.string().required("Status is required"),
    salary_type: Yup.string().required("Salary type is required"),
    expiration_date: Yup.date().required("Expiration date is required"),
  });

  const [selectedJobActivities, setSelectedJobActivities] = useState<any[]>([]);

  useEffect(() => {
    const setupProfile = async () => {
      if (countries.length === 0) return;

      if (vacancyId) {
        await fetchVacancyProfile(vacancyId);
      } else {
        const syria = countries.find((c) => c.id === 19);
        if (syria) {
          setProfileData((prev) => ({
            ...prev,
            country_id: 19,
          }));
          setSelectedCountry(19);

          if (
            profileData.job_activity_ids &&
            profileData.job_activity_ids.length > 0
          ) {
            const selectedActivities = jobActivities.filter((activity) =>
              profileData.job_activity_ids.includes(activity?.id)
            );
            setSelectedJobActivities(selectedActivities);
          }
        }
      }
    };

    setupProfile();
  }, [vacancyId, countries]);

  useEffect(() => {
    if (
      profileData.job_activity_ids &&
      profileData.job_activity_ids.length > 0
    ) {
      const selectedActivities = jobActivities.filter((activity) =>
        profileData.job_activity_ids.includes(activity?.id)
      );
      setSelectedJobActivities(selectedActivities);
    }
  }, [profileData.job_activity_ids, jobActivities]);

  useEffect(() => {
    if (cities.length > 0 && profileData.city_id && !selectedCity) {
      const cityExists = cities.find((city) => city.id === profileData.city_id);
      if (cityExists) {
        setSelectedCity(profileData.city_id);
      }
    }
  }, [cities, profileData.city_id, selectedCity]);

  const fetchVacancyProfile = async (id: string) => {
    try {
      setIsLoading(true);
      const response = await USERAXIOS.get(`/vacancies/${id}`);
      const data = response.data.data;

      if (data.country) {
        setSelectedCountry(data.country.id);
        if (data.city) {
          setSelectedCity(Number(data.city.id));
        }
      }

      const transformedData = {
        ...data,

        photo:
          typeof data.photo === "boolean"
            ? data.photo
            : data.photo === "true" || data.photo === true,
        salary_type_meta: Array.isArray(data.salary_type_meta)
          ? data.salary_type_meta[0] || {
              value: "",
              start_range: "",
              end_range: "",
            }
          : data.salary_type_meta || {
              value: "",
              start_range: "",
              end_range: "",
            },
      };

      if (data.job_activities && data.job_activities.length > 0) {
        setSelectedJobActivities(data.job_activities);
      }

      setProfileData(transformedData);
      setProfileData({
        ...transformedData,
        user_id: data.user?.id || null,
        job_type_id: data.job_type?.id || null,
        degree_type_id: data.degree_type?.id || null,
        country_id: data.country?.id || null,
        city_id: data.city?.id || null,
      });
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Failed to fetch vacancy profile",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: { color: "error" },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenderChange = (value, setFieldValue) => {
    setFieldValue("gender", value);
    if (value === "FEMALE") {
      setIsMilitaryServiceDisabled(true);
      setFieldValue("military_service", "EXEMPTED");
    } else {
      setIsMilitaryServiceDisabled(false);
    }
  };

  const getServerError = (fieldName: string) => {
    return serverErrors[fieldName]?.[0];
  };

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <section>
        <Stack direction={"row"}>
          <Stack direction={"column"}>
            <Typography
              variant="h4"
              sx={{
                textAlign: "center",
                color: "primary.main",
                marginBottom: "3rem",
              }}
            >
              <FormattedMessage
                id={vacancyId ? "Edit Vacancy" : "Add Vacancy"}
              />
            </Typography>

            <Formik
              key={JSON.stringify(profileData)}
              initialValues={profileData}
              validationSchema={validationSchema}
              onSubmit={async (values, { setSubmitting }) => {
                setSubmitting(true);
                setServerErrors({});
                try {
                  const formData = new FormData();

                  values.job_activity_ids?.forEach((id, index) => {
                    formData.append(`job_activity_ids[${index}]`, String(id));
                  });

                  if (values?.salary_type === "FIXED") {
                    formData.append(
                      "salary_type_meta[value]",
                      values?.salary_type_meta?.value || ""
                    );
                  } else if (values?.salary_type === "RANGE") {
                    formData.append(
                      "salary_type_meta[start_range]",
                      values.salary_type_meta?.start_range || ""
                    );
                    formData.append(
                      "salary_type_meta[end_range]",
                      values.salary_type_meta?.end_range || ""
                    );
                  }

                  formData.append(
                    "hide_company_name",
                    values.hide_company_name ? "1" : "0"
                  );
                  formData.append("job_of_day", values.job_of_day ? "1" : "0");
                  formData.append("photo", values.photo ? "1" : "0");

                  formData.append(
                    "vip_company",
                    values.vip_company ? "1" : "0"
                  );
                  formData.append("best_place", values.best_place ? "1" : "0");

                  if (
                    values.requirements_pdf &&
                    typeof values.requirements_pdf !== "string"
                  ) {
                    formData.append(
                      "requirements_pdf",
                      values.requirements_pdf
                    );
                  }
                  if (
                    values.details_pdf &&
                    typeof values.details_pdf !== "string"
                  ) {
                    formData.append("details_pdf", values.details_pdf);
                  }

                  formData.append("benefits", values.benefits);
                  formData.append("additional_req", values.additional_req);
                  formData.append("qualifications", values.qualifications);

                  formData.append("responsibilities", values.responsibilities);
                  formData.append(
                    "degree_type_id",
                    String(values.degree_type_id)
                  );
                  formData.append("job_type_id", String(values.job_type_id));
                  formData.append("user_id", String(values.user_id));

                  if (values.title !== null && values.title !== undefined) {
                    formData.append("title", String(values.title));
                  }
                  if (
                    values.company_description !== null &&
                    values.company_description !== undefined
                  ) {
                    formData.append(
                      "company_description",
                      String(values.company_description)
                    );
                  }
                  if (
                    values.salary_type !== null &&
                    values.salary_type !== undefined
                  ) {
                    formData.append("salary_type", String(values.salary_type));
                  }
                  formData.append(
                    "country_id",
                    String(selectedCountry || values.country_id)
                  );
                  formData.append(
                    "city_id",
                    String(selectedCity || values.city_id)
                  );
                  formData.append("email", values.email);
                  formData.append(
                    "employees_total_numbers",
                    values.employees_total_numbers
                  );
                  formData.append("duration", values.duration);
                  formData.append("military_service", values.military_service);

                  formData.append("cv_lang", values.cv_lang);
                  formData.append("expiration_date", values.expiration_date);

                  if (vacancyId) {
                    await USERAXIOS.post(`/vacancies/${vacancyId}`, formData);
                  } else {
                    await USERAXIOS.post("/vacancies", formData);
                  }

                  openSnackbar({
                    open: true,
                    message: `Vacancy ${
                      vacancyId ? "updated" : "created"
                    } successfully`,
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: "alert",
                    alert: {
                      color: "success",
                    },
                  } as SnackbarProps);

                  navigate("/vacancies");
                } catch (error: any) {
                  if (error.response.data) {
                    setServerErrors(error.response.data.message);
                  }

                  openSnackbar({
                    open: true,
                    message: `Error ${
                      vacancyId ? "updating" : "creating"
                    } vacancy`,
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: "alert",
                    alert: {
                      color: "error",
                    },
                  } as SnackbarProps);
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                errors,
                handleSubmit,
                isSubmitting,
                setFieldValue,
                touched,
                values,
              }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Box sx={{ p: 2.5 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{ marginTop: "0px", marginBottom: "2rem" }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              <FormattedMessage id="Employer Information" />
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="user-select"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Company Name" />
                                </InputLabel>
                                <FormControl
                                  fullWidth
                                  error={!!getServerError("user_id")}
                                >
                                  <InputLabel id="user-select-label">
                                    Select user
                                  </InputLabel>
                                  <Select
                                    labelId="user-select-label"
                                    id="user-select"
                                    value={values?.user_id || ""}
                                    label="Select user"
                                    onChange={(e) => {
                                      setFieldValue(
                                        "user_id",
                                        e.target.value || null
                                      );
                                    }}
                                    renderValue={(selected) => {
                                      const user = users.find(
                                        (u) => u.id === Number(selected)
                                      );
                                      return user
                                        ? user.user_name ||
                                            user.email ||
                                            String(user.id)
                                        : "";
                                    }}
                                    disabled={!!vacancyId}
                                  >
                                    {users.map((option) => (
                                      <MenuItem
                                        key={option.id}
                                        value={option.id}
                                      >
                                        {option.user_name ||
                                          option.email ||
                                          String(option.id)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {getServerError("user_id") && (
                                    <FormHelperText>
                                      {getServerError("user_id")}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="email"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Company Email" />
                                </InputLabel>
                                <Field name="email">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      id="email"
                                      type="email"
                                      placeholder="Company Email"
                                      error={
                                        !!errors.email ||
                                        !!getServerError("email")
                                      }
                                      helperText={
                                        errors.email || getServerError("email")
                                      }
                                      disabled={!!vacancyId}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={12}>
                              <Stack>
                                <InputLabel
                                  htmlFor="company_description"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="About Company" />
                                </InputLabel>
                                <Field name="company_description">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      multiline
                                      rows={4}
                                      id="company_description"
                                      placeholder="Company Description"
                                      error={
                                        !!errors.company_description ||
                                        !!getServerError("company_description")
                                      }
                                      helperText={
                                        errors.company_description ||
                                        getServerError("company_description")
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>

                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{ marginTop: "0px", marginBottom: "2rem" }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              <FormattedMessage id="Job Vacancy Information" />
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="employees_total_numbers"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Employees required number" />
                                </InputLabel>
                                <Field name="employees_total_numbers">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      type="number"
                                      id="employees_total_numbers"
                                      placeholder="Number of Employees Required"
                                      error={
                                        !!errors.employees_total_numbers ||
                                        !!getServerError(
                                          "employees_total_numbers"
                                        )
                                      }
                                      helperText={
                                        errors.employees_total_numbers ||
                                        getServerError(
                                          "employees_total_numbers"
                                        )
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="duration"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Publicshed Duration" />
                                </InputLabel>
                                <FormControl
                                  fullWidth
                                  error={!!getServerError("duration")}
                                >
                                  <Select
                                    value={values.duration}
                                    onChange={(e) =>
                                      setFieldValue("duration", e.target.value)
                                    }
                                    displayEmpty
                                  >
                                    <MenuItem value="30">30 days</MenuItem>
                                    <MenuItem value="60">60 days</MenuItem>
                                  </Select>
                                  {getServerError("duration") && (
                                    <FormHelperText>
                                      {getServerError("duration")}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </Stack>
                            </Grid>

                            <Grid item xs={12}>
                              <Stack>
                                <InputLabel
                                  htmlFor="job-activities-select"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Field of Work" />
                                </InputLabel>
                                <Autocomplete
                                  multiple
                                  id="job-activities-select"
                                  options={jobActivities}
                                  getOptionLabel={(option) =>
                                    theme.direction === "rtl"
                                      ? option.name_trans?.ar
                                      : option.name
                                  }
                                  value={selectedJobActivities}
                                  onChange={(_, newValue) => {
                                    const ids = newValue.map((item) => item.id);
                                    setFieldValue("job_activity_ids", ids);
                                    setSelectedJobActivities(newValue);
                                  }}
                                  renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                      <Chip
                                        color="primary"
                                        variant="filled"
                                        label={
                                          theme.direction === "rtl"
                                            ? option.name_trans?.ar
                                            : option.name
                                        }
                                        {...getTagProps({ index })}
                                        key={option.id}
                                      />
                                    ))
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select job activities"
                                      error={
                                        !!getServerError("job_activity_ids")
                                      }
                                      helperText={getServerError(
                                        "job_activity_ids"
                                      )}
                                    />
                                  )}
                                />
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="job-type-select"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Job Type" />
                                </InputLabel>
                                <FormControl
                                  fullWidth
                                  error={!!getServerError("job_type_id")}
                                >
                                  <InputLabel id="job-type-select-label">
                                    Select job type
                                  </InputLabel>
                                  <Select
                                    labelId="job-type-select-label"
                                    id="job-type-select"
                                    value={values?.job_type_id || ""}
                                    label="Select job type"
                                    onChange={(e) => {
                                      setFieldValue(
                                        "job_type_id",
                                        e.target.value || null
                                      );
                                    }}
                                  >
                                    {jobTypes.map((option) => (
                                      <MenuItem
                                        key={option.id}
                                        value={option.id}
                                      >
                                        {theme.direction === "rtl"
                                          ? option.name_trans?.ar
                                          : option.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {getServerError("job_type_id") && (
                                    <FormHelperText>
                                      {getServerError("job_type_id")}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="salary_type"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Salary Type" />
                                </InputLabel>
                                <FormControl fullWidth>
                                  <Select
                                    value={values.salary_type}
                                    onChange={(e) =>
                                      setFieldValue(
                                        "salary_type",
                                        e.target.value
                                      )
                                    }
                                    id="salary_type"
                                  >
                                    {salaryTypeOptions.map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {theme.direction === "rtl"
                                          ? option.arabic_label
                                          : option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {getServerError("salary_type_meta") && (
                                  <FormHelperText error>
                                    {getServerError("salary_type_meta")}
                                  </FormHelperText>
                                )}
                              </Stack>
                            </Grid>

                            {/* Salary Range Fields */}
                            {values.salary_type === "FIXED" && (
                              <Grid item xs={12} sm={6}>
                                <Stack>
                                  <InputLabel style={{ marginBottom: "10px" }}>
                                    <FormattedMessage id="Salary Value" />
                                  </InputLabel>
                                  <TextField
                                    fullWidth
                                    value={values.salary_type_meta?.value || ""}
                                    onChange={(e) => {
                                      setFieldValue("salary_type_meta", {
                                        ...values.salary_type_meta,
                                        value: e.target.value,
                                      });
                                    }}
                                    placeholder="Salary Value"
                                  />
                                </Stack>
                              </Grid>
                            )}

                            {values.salary_type === "RANGE" && (
                              <>
                                <Grid item xs={12} sm={6}>
                                  <Stack>
                                    <InputLabel
                                      style={{ marginBottom: "10px" }}
                                    >
                                      <FormattedMessage id="Min Value" />
                                    </InputLabel>
                                    <TextField
                                      fullWidth
                                      value={
                                        values.salary_type_meta?.start_range ||
                                        ""
                                      }
                                      onChange={(e) => {
                                        setFieldValue("salary_type_meta", {
                                          ...values.salary_type_meta,
                                          start_range: e.target.value,
                                        });
                                      }}
                                      placeholder="Minimum Salary"
                                    />
                                  </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Stack>
                                    <InputLabel
                                      style={{ marginBottom: "10px" }}
                                    >
                                      <FormattedMessage id="Max Value" />
                                    </InputLabel>
                                    <TextField
                                      fullWidth
                                      value={
                                        values.salary_type_meta?.end_range || ""
                                      }
                                      onChange={(e) => {
                                        setFieldValue("salary_type_meta", {
                                          ...values.salary_type_meta,
                                          end_range: e.target.value,
                                        });
                                      }}
                                      placeholder="Maximum Salary"
                                    />
                                  </Stack>
                                </Grid>
                              </>
                            )}

                            <Grid item xs={12} sm={12}>
                              <Stack>
                                <InputLabel
                                  htmlFor="responsibilities"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Responsibilities" />
                                </InputLabel>
                                <Field name="responsibilities">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      multiline
                                      rows={4}
                                      id="responsibilities"
                                      placeholder="Job Responsibilities"
                                      error={
                                        !!errors.responsibilities ||
                                        !!getServerError("responsibilities")
                                      }
                                      helperText={
                                        errors.responsibilities ||
                                        getServerError("responsibilities")
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={12}>
                              <Stack>
                                <InputLabel
                                  htmlFor="qualifications"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Qualifications" />
                                </InputLabel>
                                <Field name="qualifications">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      multiline
                                      rows={4}
                                      id="qualifications"
                                      placeholder="Required Qualifications"
                                      error={!!getServerError("qualifications")}
                                      helperText={getServerError(
                                        "qualifications"
                                      )}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={12}>
                              <Stack>
                                <InputLabel
                                  htmlFor="additional_req"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Specific Requirements" />
                                </InputLabel>
                                <Field name="additional_req">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      multiline
                                      rows={4}
                                      id="additional_req"
                                      placeholder="Additional Requirements"
                                      error={!!getServerError("additional_req")}
                                      helperText={getServerError(
                                        "additional_req"
                                      )}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={12}>
                              <Stack>
                                <InputLabel
                                  htmlFor="benefits"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Benefits" />
                                </InputLabel>
                                <Field name="benefits">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      multiline
                                      rows={4}
                                      id="benefits"
                                      placeholder="Job Benefits"
                                      error={!!getServerError("benefits")}
                                      helperText={getServerError("benefits")}
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>

                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{ marginTop: "0px", marginBottom: "2rem" }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              <FormattedMessage id="Required Employee Specifications" />
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="title"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Job Title" />
                                </InputLabel>
                                <Field name="title">
                                  {({ field }: { field: any }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      id="title"
                                      placeholder="Job Title"
                                      error={
                                        !!errors.title ||
                                        !!getServerError("title")
                                      }
                                      helperText={
                                        errors.title || getServerError("title")
                                      }
                                    />
                                  )}
                                </Field>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="degree-type-select"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Minimum Education Level" />
                                </InputLabel>
                                <FormControl
                                  fullWidth
                                  error={!!getServerError("degree_type_id")}
                                >
                                  <InputLabel id="degree-type-select-label">
                                    Select degree type
                                  </InputLabel>
                                  <Select
                                    labelId="degree-type-select-label"
                                    id="degree-type-select"
                                    value={values?.degree_type_id || ""}
                                    label="Select degree type"
                                    onChange={(e) => {
                                      setFieldValue(
                                        "degree_type_id",
                                        e.target.value || null
                                      );
                                    }}
                                  >
                                    {degreeTypes.map((option) => (
                                      <MenuItem
                                        key={option.id}
                                        value={option.id}
                                      >
                                        {theme.direction === "rtl"
                                          ? option.name_trans?.ar
                                          : option.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {getServerError("degree_type_id") && (
                                    <FormHelperText>
                                      {getServerError("degree_type_id")}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="gender"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Gender" />
                                </InputLabel>
                                <FormControl
                                  fullWidth
                                  error={
                                    touched.gender && Boolean(errors.gender)
                                  }
                                >
                                  <Select
                                    value={values.gender}
                                    onChange={(e) =>
                                      handleGenderChange(
                                        e.target.value,
                                        setFieldValue
                                      )
                                    }
                                    id="gender"
                                  >
                                    {genderOptions.map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {theme.direction === "rtl"
                                          ? option.arabic_label
                                          : option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {touched.gender && errors.gender && (
                                    <FormHelperText>
                                      {errors.gender}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="military_service"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Military Service" />
                                </InputLabel>
                                <FormControl fullWidth>
                                  <Select
                                    value={values.military_service}
                                    onChange={(e) =>
                                      setFieldValue(
                                        "military_service",
                                        e.target.value
                                      )
                                    }
                                    disabled={isMilitaryServiceDisabled}
                                    id="military_service"
                                  >
                                    {militaryServiceOptions.map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {theme.direction === "rtl"
                                          ? option.arabic_label
                                          : option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>

                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{ marginTop: "0px", marginBottom: "2rem" }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              <FormattedMessage id="Location Information" />
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Stack spacing={1}>
                                <Typography>
                                  <FormattedMessage id="Country" />
                                </Typography>
                                <Autocomplete
                                  id="country-select"
                                  options={countries || []}
                                  getOptionLabel={(option: Country) =>
                                    theme.direction === "rtl"
                                      ? option.name_trans.ar
                                      : option.name
                                  }
                                  value={
                                    countries?.find(
                                      (country) =>
                                        country.id === values.country_id
                                    ) || null
                                  }
                                  onChange={(_, newValue) => {
                                    const selectedCountryId =
                                      newValue?.id || null;
                                    setFieldValue(
                                      "country_id",
                                      selectedCountryId
                                    );
                                    setFieldValue("city_id", null);
                                    setSelectedCountry(selectedCountryId);
                                    setSelectedCity(null);
                                    if (newValue) {
                                      setCountrySearchTerm("");
                                    }
                                  }}
                                  onInputChange={(
                                    event,
                                    newInputValue,
                                    reason
                                  ) => {
                                    if (reason === "input") {
                                      setCountrySearchTerm(newInputValue);
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select country"
                                      error={
                                        (touched.country_id &&
                                          Boolean(errors.country_id)) ||
                                        !!getServerError("country_id")
                                      }
                                      helperText={
                                        errors.country_id ||
                                        getServerError("country_id")
                                      }
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                          <>
                                            {loadingCountries && (
                                              <CircularProgress
                                                color="inherit"
                                                size={20}
                                              />
                                            )}
                                            {params.InputProps.endAdornment}
                                          </>
                                        ),
                                      }}
                                    />
                                  )}
                                  ListboxProps={{
                                    onScroll: (event) => {
                                      const listboxNode = event.currentTarget;
                                      const threshold = 100;
                                      if (
                                        listboxNode.scrollHeight -
                                          listboxNode.scrollTop <=
                                          listboxNode.clientHeight +
                                            threshold &&
                                        hasMoreCountries &&
                                        !loadingCountries
                                      ) {
                                        loadMoreCountries();
                                      }
                                    },
                                  }}
                                  loading={loadingCountries}
                                  filterOptions={(x) => x}
                                  noOptionsText={
                                    debouncedCountrySearchTerm.length >= 2
                                      ? "No countries found"
                                      : "Type to search countries"
                                  }
                                />
                                {countriesError && (
                                  <FormHelperText error>
                                    Error loading countries. Please try again.
                                  </FormHelperText>
                                )}
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack spacing={1}>
                                <Typography>
                                  <FormattedMessage id="City" />
                                </Typography>
                                <Autocomplete
                                  id="city-select"
                                  options={cities}
                                  getOptionLabel={(option: City) =>
                                    theme.direction === "rtl"
                                      ? option.name_trans.ar
                                      : option.name
                                  }
                                  value={
                                    cities.find(
                                      (city) => city.id === selectedCity
                                    ) || null
                                  }
                                  onChange={(_, newValue) => {
                                    const selectedCityId = newValue?.id || null;
                                    setFieldValue("city_id", selectedCityId);
                                    setSelectedCity(selectedCityId);
                                    if (newValue) {
                                      setCitySearchTerm("");
                                    }
                                  }}
                                  onInputChange={(
                                    event,
                                    newInputValue,
                                    reason
                                  ) => {
                                    if (reason === "input") {
                                      setCitySearchTerm(newInputValue);
                                    }
                                  }}
                                  disabled={!selectedCountry}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select city"
                                      error={
                                        (touched.city_id &&
                                          Boolean(errors.city_id)) ||
                                        !!getServerError("city_id")
                                      }
                                      helperText={
                                        errors.city_id ||
                                        getServerError("city_id")
                                      }
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                          <>
                                            {loadingCities && (
                                              <CircularProgress
                                                color="inherit"
                                                size={20}
                                              />
                                            )}
                                            {params.InputProps.endAdornment}
                                          </>
                                        ),
                                      }}
                                    />
                                  )}
                                  ListboxProps={{
                                    onScroll: (event) => {
                                      const listboxNode = event.currentTarget;
                                      const threshold = 100;
                                      if (
                                        listboxNode.scrollHeight -
                                          listboxNode.scrollTop <=
                                          listboxNode.clientHeight +
                                            threshold &&
                                        hasMoreCities &&
                                        !loadingCities
                                      ) {
                                        loadMoreCities();
                                      }
                                    },
                                  }}
                                  loading={loadingCities}
                                  filterOptions={(x) => x}
                                  noOptionsText={
                                    debouncedCitySearchTerm.length >= 2
                                      ? "No cities found"
                                      : "Type to search cities"
                                  }
                                />
                                {citiesError && (
                                  <FormHelperText error>
                                    Error loading cities. Please try again.
                                  </FormHelperText>
                                )}
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>

                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{ marginTop: "0px", marginBottom: "2rem" }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              <FormattedMessage id="Settings" />
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="vacancy_lang"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Vacancy Language" />
                                </InputLabel>
                                <FormControl
                                  fullWidth
                                  error={
                                    touched.vacancy_lang &&
                                    Boolean(errors.vacancy_lang)
                                  }
                                >
                                  <Select
                                    value={values.vacancy_lang}
                                    onChange={(e) =>
                                      setFieldValue(
                                        "vacancy_lang",
                                        e.target.value
                                      )
                                    }
                                    id="vacancy_lang"
                                    disabled={!!vacancyId}
                                  >
                                    {vacancyLangOptions.map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {theme.direction === "rtl"
                                          ? option.arabic_label
                                          : option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {touched.vacancy_lang &&
                                    errors.vacancy_lang && (
                                      <FormHelperText>
                                        {errors.vacancy_lang}
                                      </FormHelperText>
                                    )}
                                </FormControl>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="status"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Status" />
                                </InputLabel>
                                <FormControl
                                  fullWidth
                                  error={
                                    touched.status && Boolean(errors.status)
                                  }
                                >
                                  <Select
                                    value={values.status}
                                    onChange={(e) =>
                                      setFieldValue("status", e.target.value)
                                    }
                                    id="status"
                                  >
                                    {statusOptions.map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {theme.direction === "rtl"
                                          ? option.arabic_label
                                          : option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {touched.status && errors.status && (
                                    <FormHelperText>
                                      {errors.status}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="cv_lang"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Resume cv_language" />
                                </InputLabel>
                                <Select
                                  value={values.cv_lang}
                                  onChange={(e) =>
                                    setFieldValue("cv_lang", e.target.value)
                                  }
                                  id="cv_lang"
                                >
                                  {vacancyResumeLangOptions.map((option) => (
                                    <MenuItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {theme.direction === "rtl"
                                        ? option.arabic_label
                                        : option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel
                                  htmlFor="expiration_date"
                                  style={{ marginBottom: "10px" }}
                                >
                                  <FormattedMessage id="Expiration Date" />
                                </InputLabel>
                                <Field name="expiration_date">
                                  {({
                                    field,
                                    form,
                                  }: {
                                    field: any;
                                    form: any;
                                  }) => (
                                    <DatePicker
                                      style={{ width: "100%" }}
                                      value={
                                        field.value ? dayjs(field.value) : null
                                      }
                                      onChange={(date) => {
                                        form.setFieldValue(
                                          field.name,
                                          date
                                            ? date.format("YYYY-MM-DD")
                                            : null
                                        );
                                      }}
                                    />
                                  )}
                                </Field>
                                {(touched.expiration_date &&
                                  errors.expiration_date) ||
                                getServerError("expiration_date") ? (
                                  <FormHelperText error>
                                    {errors.expiration_date ||
                                      getServerError("expiration_date")}
                                  </FormHelperText>
                                ) : null}
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel style={{ marginBottom: "10px" }}>
                                  <FormattedMessage id="Hide Company Name" />
                                </InputLabel>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={values.hide_company_name}
                                      onChange={(e) => {
                                        setFieldValue(
                                          "hide_company_name",
                                          e.target.checked
                                        );
                                      }}
                                    />
                                  }
                                  label={
                                    values.hide_company_name ? (
                                      <FormattedMessage id="Active" />
                                    ) : (
                                      <FormattedMessage id="Inactive" />
                                    )
                                  }
                                />
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel style={{ marginBottom: "10px" }}>
                                  <FormattedMessage id="Applicant Photo Required" />
                                </InputLabel>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={values.photo}
                                      onChange={(e) => {
                                        setFieldValue(
                                          "photo",
                                          e.target.checked
                                        );
                                      }}
                                    />
                                  }
                                  label={
                                    values.photo ? (
                                      <FormattedMessage id="Required" />
                                    ) : (
                                      <FormattedMessage id="Not Required" />
                                    )
                                  }
                                />
                                {getServerError("photo") && (
                                  <FormHelperText error>
                                    {getServerError("photo")}
                                  </FormHelperText>
                                )}
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel style={{ marginBottom: "10px" }}>
                                  <FormattedMessage id="Job of the Day" />
                                </InputLabel>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={values.job_of_day}
                                      onChange={(e) => {
                                        setFieldValue(
                                          "job_of_day",
                                          e.target.checked
                                        );
                                      }}
                                    />
                                  }
                                  label={
                                    values.job_of_day ? (
                                      <FormattedMessage id="Yes" />
                                    ) : (
                                      <FormattedMessage id="No" />
                                    )
                                  }
                                />
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>

                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{ marginTop: "0px", marginBottom: "2rem" }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              <FormattedMessage id="Marketing" />
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel style={{ marginBottom: "10px" }}>
                                  <FormattedMessage id="VIP Company" />
                                </InputLabel>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={values.vip_company}
                                      onChange={(e) => {
                                        setFieldValue(
                                          "vip_company",
                                          e.target.checked
                                        );
                                      }}
                                    />
                                  }
                                  label={
                                    values.vip_company ? (
                                      <FormattedMessage id="Yes" />
                                    ) : (
                                      <FormattedMessage id="No" />
                                    )
                                  }
                                />
                              </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Stack>
                                <InputLabel style={{ marginBottom: "10px" }}>
                                  <FormattedMessage id="Best Place" />
                                </InputLabel>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={values.best_place}
                                      onChange={(e) => {
                                        setFieldValue(
                                          "best_place",
                                          e.target.checked
                                        );
                                      }}
                                    />
                                  }
                                  label={
                                    values.best_place ? (
                                      <FormattedMessage id="Yes" />
                                    ) : (
                                      <FormattedMessage id="No" />
                                    )
                                  }
                                />
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>

                      <Grid item xs={12} sm={12}>
                        <MainCard
                          style={{ marginTop: "0px", marginBottom: "2rem" }}
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              <FormattedMessage id="Media & Documents" />
                            </Typography>
                          }
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <Stack>
                                <Typography style={{ marginBottom: "10px" }}>
                                  <FormattedMessage id="Requirements PDF" />
                                </Typography>
                                <PdfUploader
                                  id="requirements-pdf"
                                  file={values.requirements_pdf}
                                  onChange={(e, setFieldValue) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      setFieldValue("requirements_pdf", file);
                                    }
                                  }}
                                  setFieldValue={setFieldValue}
                                  fieldName="requirements_pdf"
                                  label="Upload Requirements PDF"
                                  error={
                                    touched.requirements_pdf
                                      ? errors.requirements_pdf
                                      : undefined
                                  }
                                />
                              </Stack>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Stack>
                                <Typography style={{ marginBottom: "10px" }}>
                                  <FormattedMessage id="Details PDF" />
                                </Typography>
                                <PdfUploader
                                  id="details-pdf"
                                  file={values.details_pdf}
                                  onChange={(e, setFieldValue) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      setFieldValue("details_pdf", file);
                                    }
                                  }}
                                  setFieldValue={setFieldValue}
                                  fieldName="details_pdf"
                                  label="Upload Details PDF"
                                  error={
                                    touched.details_pdf
                                      ? errors.details_pdf
                                      : undefined
                                  }
                                />
                              </Stack>
                            </Grid>
                          </Grid>
                        </MainCard>
                      </Grid>
                    </Grid>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    marginTop="2rem"
                  >
                    <Grid container justifyContent="center" alignItems="center">
                      <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                        <Grid item md={2} />
                        <Grid item md={4}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => navigate("/vacancies")}
                          >
                            <FormattedMessage id="Cancel" />
                          </Button>
                        </Grid>
                        <Grid item md={4}>
                          <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            <FormattedMessage
                              id={vacancyId ? "Update" : "Create"}
                            />
                          </Button>
                        </Grid>
                        <Grid item md={2} />
                      </Stack>
                    </Grid>
                  </Stack>
                </form>
              )}
            </Formik>
          </Stack>
        </Stack>
      </section>
    </>
  );
};

export default AddEditVacancy;
